import { useCallback, useEffect, useState } from "react";
import { Grid, useMediaQuery } from "@mui/material";
import { settings } from "app";
import { FunctionField, Labeled, ReferenceField, SimpleShowLayout, TextField, SelectField } from "react-admin";
import { GenderOptionsId, PersonIdTypes } from "utils/choices";
import { Gender } from "utils/constants";
import { accountRequests } from "api/accountApi";
import SetYoungChildrenMothersDialog from "./SetYoungChildrenMothersDialog";
import UnsetYoungChildrenMothersDialog from "./UnsetYoungChildrenMothersDialog";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import EditPersonalInfoModal from "../Modals/PersonalInfoModal";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import EditEmailDialog from "components/Users/components/EditModals/EditEmailDialog";
import PasswordResetAction from "components/Users/components/PasswordResetAction/PasswordResetAction";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import dataProvider from "server/dataProvider";
import IbanModal from "../Modals/IbanModal";

const PersonalData = ({ record }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const userPermissions = getPermissions();
  const hasEditUsersPermission = checkPermission(userPermissions, permissions.canUpdateUsers);
  const [iban, setIban] = useState(null);

  const fetchIban = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(studentRequests.getStudentIban(record?.studentId), null, "GET");
      setIban(data || {});
    } catch (err) {
      // 404 means the student has no IBAN yet — fall back to an empty record
      console.error(err);
      setIban({});
    }
  }, [record?.studentId]);

  useEffect(() => {
    if (!record?.studentId) return;
    fetchIban();
  }, [record?.studentId, fetchIban]);

  //Име, презиме, фамилия, телефон, гражданство, имейл.
  return (
    <SimpleShowLayout>
      <Grid
        spacing={2}
        container
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SecuredComponent permission={permissions.canUpdateUsers}>
            <EditPersonalInfoModal record={record} />
          </SecuredComponent>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Име">
            <TextField
              source="userInfo.firstName"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Презиме">
            <TextField
              source="userInfo.secondName"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Фамилия">
            <TextField
              source="userInfo.lastName"
              emptyText="-"
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Име на латиница">
            <TextField
              source="userInfo.firstNameEn"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Презиме на латиница">
            <TextField
              source="userInfo.secondNameEn"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Фамилия на латиница">
            <TextField
              source="userInfo.lastNameEn"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label={"Tип идентификатор"}>
            <SelectField
              source="userInfo.personIdType"
              choices={PersonIdTypes}
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label={PersonIdTypes.find(x => x.id === record?.userInfo?.personIdType)?.name}>
            <TextField
              source="userInfo.personId"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Пол">
            <FunctionField
              render={record => GenderOptionsId.find(option => option.id === record?.userInfo?.gender)?.name}
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
        >
          <Labeled label="Телефон">
            <TextField
              source="userInfo.contactPhone"
              emptyText="-"
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Дата на раждане">
            <TextField
              source="userInfo.birthDateFormatted"
              emptyText="-"
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Град на раждане">
            {record?.userInfo?.birthSettlementId ? (
              <ReferenceField
                source="userInfo.birthSettlementId"
                reference={administrationRequests.getSettlementsSelect()}
              >
                <TextField source="nameBg" />
              </ReferenceField>
            ) : (
              <TextField
                source="userInfo.birthSettlement"
                emptyText="-"
              />
            )}
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Държава на раждане">
            <ReferenceField
              source="userInfo.birthCountryId"
              reference={administrationRequests.getCountriesSelect()}
            >
              <TextField source="nameBg" />
            </ReferenceField>
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Тип гражданство">
            <ReferenceField
              source="userInfo.citizenship"
              reference={administrationRequests.getCitizenshipSelect()}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Основно гражданство">
            <ReferenceField
              source="userInfo.primaryCitizenshipCountryId"
              reference={administrationRequests.getCountriesSelect()}
            >
              <TextField source="nameBg" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Второ Гражданство">
            <ReferenceField
              source="userInfo.secondaryCitizenshipCountryId"
              reference={administrationRequests.getCountriesSelect()}
              emptyText="-"
            >
              <TextField source="nameBg" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Имейл">
            <TextField
              source="userInfo.email"
              emptyText="-"
            />
          </Labeled>
          <div>
            <EditEmailDialog
              currentEmailProp={record?.userInfo?.email}
              variant={"contained"}
              request={accountRequests.changeUserEmail(record?.userId)}
              source={"userInfo.email"}
              hasEditUsersPermission={hasEditUsersPermission}
              userRecord={record}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <Labeled label="Имейл на студента">
            <TextField
              source="userInfo.externalEmail"
              emptyText="-"
            />
          </Labeled>
          {/* Under the university (Google Workspace) email it resets - by studentId, not userId. */}
          <SecuredComponent permission={permissions.canResetStudentsGooglePasswords}>
            <div>
              <PasswordResetAction
                label="Изпрати нова Google парола"
                variant="contained"
                size="small"
                request={studentRequests.resetStudentGooglePassword(record?.studentId)}
                description="Ще бъде генерирана еднократна парола за университетския Google акаунт на студента и изпратена, заедно с Google адреса, на имейла му в платформата. При следващото влизане студентът ще трябва да я смени."
                successMessage="Новата Google парола беше изпратена на имейла на студента в платформата."
              />
            </div>
          </SecuredComponent>
        </Grid>
        <SecuredComponent permission={permissions.canPreviewStudentInfo}>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="IBAN">
              <TextField
                source="ibanNumber"
                record={iban}
                emptyText="Няма посочен"
              />
            </Labeled>
            <SecuredComponent permission={permissions.canUpdateStudentInfo}>
              <div>
                <IbanModal
                  iban={iban}
                  record={record}
                  fetchIban={fetchIban}
                />
              </div>
            </SecuredComponent>
          </Grid>
        </SecuredComponent>

        <Grid
          item
          xs={12}
          md={4}
        >
          <SecuredComponent permission={permissions.canMarkYoungChildrenMothers}>
            <FunctionField
              render={record =>
                record?.userInfo?.gender === Gender.Female &&
                !record?.youngChildrenMotherInfo && <SetYoungChildrenMothersDialog studentId={record?.studentId} />
              }
            />

            <FunctionField
              render={record =>
                record?.userInfo?.gender === Gender.Female &&
                record?.youngChildrenMotherInfo && <UnsetYoungChildrenMothersDialog studentId={record?.studentId} />
              }
            />
          </SecuredComponent>
        </Grid>
      </Grid>
    </SimpleShowLayout>
  );
};
export default PersonalData;
